import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Alert, LoadingSpinner, PageContainer, QueryErrorAlert, TabList, TabPanel, TabsRoot } from '@shared/components';
import { usePermissions } from '@/features/auth';
import { buildPurchaseView } from '../services/purchaseSnapshot';
import { useAgreementQuery } from '../hooks/useAgreementQuery';
import { useAgreementHistory } from '../hooks/useAgreementHistory';
import { useAgreementNotesTab } from '../hooks/useAgreementNotesTab';
import { useAgreementDocumentsTab } from '../hooks/useAgreementDocumentsTab';
import { useAgreementCustomerTab } from '../hooks/useAgreementCustomerTab';
import { useAgreementAuthorisedContactsPanel } from '../hooks/useAgreementAuthorisedContactsPanel';
import { useAgreementInvoicing } from '../hooks/useAgreementInvoicing';
import { useAgreementFinancials } from '../hooks/useAgreementFinancials';
import { useHeaderAvailableBalance } from '../hooks/useHeaderAvailableBalance';
import { useAgreementLeftToPayQuery } from '../hooks/useAgreementLeftToPayQuery';
import { canInvoice } from '../utils/invoicingRoute';
import { usePaymentLinkWatch } from '../hooks/usePaymentLinkWatch';
import { useCancellationProgressWatch } from '../hooks/useCancellationProgressWatch';
import { paymentReceivedNotice } from '../utils/motorplanPaymentCopy';
import { leftToPayDisplay } from '../utils/leftToPayDisplay';
import { isListPanelFocusable } from '../utils/listPanelFocus';
import { resolveActiveTab, visibleAgreementTabs, type AgreementTab } from '../utils/agreementTabs';
import { AgreementHeader } from '../components/AgreementHeader';
import { AgreementOverviewTab } from '../components/AgreementOverviewTab';
import { AgreementCustomerTab } from '../components/AgreementCustomerTab';
import { ChangeRegistrationModal } from '../components/servicing/ChangeRegistrationModal/ChangeRegistrationModal';
import { useAgreementVehicleRegistration } from '../hooks/useAgreementVehicleRegistration';
import { useAgreementVehicleAmendments } from '../hooks/useAgreementVehicleAmendments';
import { AgreementFinancialsTab } from '../components/AgreementFinancialsTab/AgreementFinancialsTab';
import { AgreementHistoryTab } from '../components/AgreementHistoryTab';
import { AgreementNotesTab } from '../components/AgreementNotesTab';
import { AgreementDocumentsSection } from '../components/AgreementDocumentsTab/AgreementDocumentsSection';
import { AgreementInvoicingTab } from '../components/AgreementInvoicingTab';
import { AgreementDraftNotice } from '../components/AgreementDraftNotice';
import { AgreementManageTab } from '../components/manage/AgreementManageTab';
import { manageSections } from '../utils/cancellationSection';
import { AddNoteModal } from '../components/servicing/AddNoteModal/AddNoteModal';

export function AgreementDetail() {
    const { id } = useParams<{ id: string }>();
    // The customer edit page sends the reader back to the tab they left.
    const returnedToTab = (useLocation().state as { tab?: AgreementTab } | null)?.tab;
    const [selectedTab, setActiveTab] = useState<AgreementTab>(returnedToTab ?? 'overview');
    const agreementQuery = useAgreementQuery(id);
    const history = useAgreementHistory(id);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
    const vehicleRegistration = useAgreementVehicleRegistration(agreementQuery.data);
    const vehicleAmendments = useAgreementVehicleAmendments(agreementQuery.data?.vehicleId);
    const notesTab = useAgreementNotesTab(id, selectedTab === 'notes', agreementQuery.data?.status);
    const customerTab = useAgreementCustomerTab(agreementQuery.data, selectedTab === 'customer');
    const contacts = useAgreementAuthorisedContactsPanel(id, agreementQuery.data?.status);
    const canAddInvoice = usePermissions().hasPermission({ resource: 'invoice', action: 'add' });
    const documentsTab = useAgreementDocumentsTab(id, selectedTab === 'documents', agreementQuery.data?.agreementRef);
    const canManage = manageSections(agreementQuery.data?.cancelAction).length > 0;
    const tabs = visibleAgreementTabs({ canBrowseNotes: notesTab.canBrowse, canBrowseDocuments: documentsTab.canBrowse, canAddInvoice, canManage });
    const activeTab = resolveActiveTab(selectedTab, tabs);

    // Gated on the tab being active: a dealer who never opens Invoicing
    // never fires any of its requests.
    const invoicingActive = activeTab === 'invoicing';
    const invoicing = useAgreementInvoicing(id, agreementQuery.data, invoicingActive);

    const financials = useAgreementFinancials(id, activeTab === 'financials');
    const availableBalance = useHeaderAvailableBalance(id, agreementQuery.data?.status);
    const cancellationWatch = useCancellationProgressWatch(id, agreementQuery.data);
    const elemisRef = agreementQuery.data?.elemisAgreementRef;
    const leftToPayQuery = useAgreementLeftToPayQuery(id, elemisRef);
    const receivedPayment = usePaymentLinkWatch(id, canInvoice(agreementQuery.data?.invoicingRoute) && !!elemisRef);
    const leftToPay = leftToPayDisplay({
        hasElemisReference: elemisRef !== undefined && elemisRef !== null,
        isLoading: leftToPayQuery.isLoading,
        isError: leftToPayQuery.isError,
        data: leftToPayQuery.data,
    });

    const focusablePanel = activeTab === 'overview'
        ? contacts.focusablePanel && vehicleAmendments.focusablePanel
        : activeTab === 'customer'
        ? customerTab.focusablePanel
        : activeTab === 'notes'
        ? notesTab.focusablePanel
        : activeTab === 'history'
        ? isListPanelFocusable({
            isLoading: history.query.isLoading,
            isError: history.query.isError,
            hasFocusableAction: false,
            lastPage: history.query.data?.meta.lastPage,
        })
        : activeTab === 'documents'
        ? documentsTab.focusablePanel
        : activeTab === 'financials'
        ? financials.focusablePanel
        : !invoicingActive || invoicing.panelFocusable;

    /** Snapshot identity is stable under React Query's `structuralSharing`, so this
     *  only recomputes when the snapshot actually changes. */
    const purchase = useMemo(
        () => buildPurchaseView(agreementQuery.data?.purchaseSnapshot),
        [agreementQuery.data?.purchaseSnapshot],
    );

    if (!id) {
        return (
            <PageContainer>
                <QueryErrorAlert message="Invalid agreement reference." />
            </PageContainer>
        );
    }

    if (agreementQuery.isLoading) {
        return (
            <PageContainer>
                <LoadingSpinner message="Loading agreement" className="p-4" />
            </PageContainer>
        );
    }

    if (agreementQuery.isError || !agreementQuery.data) {
        return (
            <PageContainer>
                <QueryErrorAlert
                    message="Agreement not found or failed to load."
                    onRetry={() => agreementQuery.refetch()}
                    isRetrying={agreementQuery.isFetching}
                />
            </PageContainer>
        );
    }

    const agreement = agreementQuery.data;

    return (
        <TabsRoot
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            idPrefix="agreement-detail"
            ariaLabel="Agreement sections"
            variant="header"
            focusablePanel={focusablePanel}
        >
            <AgreementHeader agreement={agreement} availableBalance={availableBalance} leftToPay={leftToPay} tabs={<TabList />}>
                <AgreementDraftNotice agreementId={agreement.id} status={agreement.status} />
                {receivedPayment && (
                    <Alert variant="success" role="status">
                        <p>{paymentReceivedNotice(receivedPayment)}</p>
                    </Alert>
                )}
            </AgreementHeader>

            <PageContainer>
                <TabPanel>
                    {activeTab === 'overview' && (
                        <AgreementOverviewTab
                            agreement={agreement}
                            purchase={purchase}
                            holder={customerTab.holder}
                            onViewCustomer={() => setActiveTab('customer')}
                            registration={vehicleRegistration.view}
                            registrationSource={vehicleRegistration.source}
                            canChangeRegistration={vehicleRegistration.canChangeRegistration}
                            onChangeRegistration={() => setRegistrationDialogOpen(true)}
                            vehicleAmendments={vehicleAmendments}
                            contacts={contacts}
                        />
                    )}
                    {activeTab === 'customer' && (
                        <AgreementCustomerTab agreementId={agreement.id} customer={customerTab} />
                    )}
                    {activeTab === 'financials' && (
                        <AgreementFinancialsTab
                            view={financials.statementView}
                            isLoading={financials.isLoading}
                            isError={financials.isError}
                            notActivated={financials.notActivated}
                            rows={financials.rows}
                            futureCount={financials.futureCount}
                            showUnreconciled={financials.showUnreconciled}
                            onShowUnreconciledChange={financials.setShowUnreconciled}
                            onRetry={() => financials.refetch()}
                        />
                    )}
                    {activeTab === 'history' && (
                        <AgreementHistoryTab
                            isLoading={history.query.isLoading}
                            isError={history.query.isError}
                            history={history.history}
                            currentPage={history.page}
                            onRetry={() => history.query.refetch()}
                            onPageChange={history.setPage}
                        />
                    )}
                    {activeTab === 'notes' && (
                        <AgreementNotesTab
                            isLoading={notesTab.notes.query.isLoading}
                            isError={notesTab.notes.query.isError}
                            notes={notesTab.notes.query.data}
                            currentPage={notesTab.notes.page}
                            status={agreement.status}
                            onRetry={() => notesTab.notes.query.refetch()}
                            onPageChange={notesTab.notes.setPage}
                            onAddNote={() => setNoteDialogOpen(true)}
                        />
                    )}
                    {activeTab === 'documents' && <AgreementDocumentsSection documents={documentsTab} />}
                    {activeTab === 'invoicing' && (
                        <AgreementInvoicingTab key={agreement.id} agreement={agreement} invoicing={invoicing} />
                    )}
                    {activeTab === 'manage' && <AgreementManageTab key={agreement.id} agreement={agreement} cancellationWatch={cancellationWatch} />}
                </TabPanel>
            </PageContainer>

            <AddNoteModal agreementId={agreement.id} isOpen={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} />

            {vehicleRegistration.vehicleId && (
                <ChangeRegistrationModal
                    agreementId={agreement.id}
                    vehicleId={vehicleRegistration.vehicleId}
                    isOpen={registrationDialogOpen}
                    onClose={() => setRegistrationDialogOpen(false)}
                />
            )}
        </TabsRoot>
    );
}
